import mixpanel, { Config, Dict } from "mixpanel-browser"

import { EventENUM } from "./types"

const CONFIG: Partial<Config> = {
  api_host: "https://wet.whimstay.com",
  cdn: `https://wet.whimstay.com/cdn`,
  ignore_dnt: true,
}

const mixpanels = mixpanel.init(
  process.env.MIXPANEL_TOKEN || process.env.REACT_APP_MIXPANEL_TOKEN || "",
  CONFIG,
  "versionV2",
)

class Mixpanel {
  // eslint-disable-next-line class-methods-use-this
  private identify(id: string | number) {
    mixpanels.identify(`${id}`)
  }

  // eslint-disable-next-line class-methods-use-this
  private track(name: string, props: Dict = {}) {
    mixpanels.track(name, props)
  }

  private people = {
    set: (props: Dict) => {
      mixpanels.people.set(props)
    },
    increment: (props: Dict | string) => {
      mixpanels.people.increment(props)
    },
  }

  // eslint-disable-next-line class-methods-use-this
  private reset() {
    mixpanels.reset()
  }

  IDENTIFY(id: string | number) {
    this.identify(id)
  }

  // to reset current mixpanel session.
  RESET_USER() {
    this.reset()
  }

  // to set users data in mixpanel
  SET_PEOPLE(data: Dict) {
    this.people.set(data)
  }

  // when sign-up is completed
  RBO_USER_SIGNUP() {
    this.track(EventENUM.RBO_USER_SIGNUP)
  }

  //  when user successfully logged in
  RBO_USER_LOGIN() {
    this.track(EventENUM.RBO_USER_LOGIN)
  }

  // User syncs properties for the first time
  RBO_PROFILE_COMPLETED() {
    this.track(EventENUM.RBO_PROFILE_COMPLETED)
  }

  // closes tab after password reset or verify profile
  RBO_ONBOARDING_EXIT() {
    this.track(EventENUM.RBO_ONBOARDING_EXIT)
  }

  // User successfully connects their Airbnb profile
  RBO_AIRBNB_PROFILE_CONNECTED() {
    this.track(EventENUM.RBO_AIRBNB_PROFILE_CONNECTED)
  }

  // An error occurs during profile setup
  RBO_PROFILE_ERROR(data: { errorType?: string }) {
    this.track(EventENUM.RBO_PROFILE_ERROR, {
      "error type": data?.errorType || "NA",
    })
  }

  // A new property is listed
  RBO_PROPERTY_ADDED(data: { propertyId?: string; newPropertyCount?: number }) {
    this.track(EventENUM.RBO_PROPERTY_ADDED, {
      "property id": data?.propertyId || "NA",
      "new property count": data?.newPropertyCount || 0,
    })
  }

  // System starts pulling property data
  RBO_PROPERTY_SYNC_STARTED(data: { propertyId?: string }) {
    this.track(EventENUM.RBO_PROPERTY_SYNC_STARTED, {
      "property id": data?.propertyId || "NA",
    })
  }

  // Property data sync completes successfully or fails
  RBO_PROPERTY_SYNC_COMPLETED(data: { propertyId?: string }) {
    this.track(EventENUM.RBO_PROPERTY_SYNC_COMPLETED, {
      "property id": data?.propertyId || "NA",
    })
  }

  // User updates property details
  RBO_PROPERTY_EDITED(data: { propertyId?: string; changeType?: string }) {
    this.track(EventENUM.RBO_PROPERTY_EDITED, {
      "property id": data?.propertyId || "NA",
      "change type": data?.changeType || "NA",
    })
  }

  // User modifies property price
  RBO_PRICING_UPDATED(data: { propertyId?: string }) {
    this.track(EventENUM.RBO_PRICING_UPDATED, {
      "property id": data?.propertyId || "NA",
    })
  }

  // User blocks availability
  RBO_AVAILABILITY_BLOCKED(data: { propertyId?: string }) {
    this.track(EventENUM.RBO_AVAILABILITY_BLOCKED, {
      "property id": data?.propertyId || "NA",
    })
  }

  // User unblocks availability
  RBO_AVAILABILITY_UNBLOCKED(data: { propertyId?: string }) {
    this.track(EventENUM.RBO_AVAILABILITY_UNBLOCKED, {
      "property id": data?.propertyId || "NA",
    })
  }

  // User visit dashboard page
  RBO_DASHBOARD_VISIT() {
    this.track(EventENUM.RBO_DASHBOARD_VISIT)
  }

  // User clicks support/help
  RBO_SUPPORT_CLICKED() {
    this.track(EventENUM.RBO_SUPPORT_CLICKED)
  }

  // User logout
  RBO_USER_LOGOUT() {
    this.track(EventENUM.RBO_USER_LOGOUT)
  }
}

export const mixpanelClient = new Mixpanel()
